import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Truck, Calendar, CreditCard, ArrowRight, CheckCircle } from 'lucide-react';

/**
 * Configurador de Orçamento em 3 Passos — CRO mobile-first.
 * Permite ao utilizador escolher equipamento, datas e local de entrega
 * rapidamente, redirecionando para a página de reserva completa.
 */
export default function QuoteConfigurator({ lang = 'pt' }) {
  const [step, setStep] = useState(1);
  const [equipment, setEquipment] = useState('');
  const [city, setCity] = useState('');
  const [duration, setDuration] = useState('');

  const t = {
    pt: {
      title: 'Peça o seu orçamento em 3 passos',
      step1: 'Escolha o equipamento',
      step2: 'Onde e quando?',
      step3: 'Confirmar',
      equipmentLabel: 'Que equipamento precisa?',
      cityLabel: 'Cidade / local de entrega',
      durationLabel: 'Duração do aluguer',
      next: 'Próximo',
      back: 'Voltar',
      getQuote: 'Ver disponibilidade',
      options: {
        wheelchair: 'Cadeira de rodas manual',
        electric: 'Cadeira de rodas elétrica',
        scooter: 'Scooter de mobilidade',
        bed: 'Cama articulada',
        walker: 'Andarilho',
        bath: 'Cadeira de banho'
      },
      durations: { day: '1 dia', week: '1 semana', month: '1 mês', custom: 'Mais de 1 mês' },
      summary: 'Resumo do pedido',
      badge: 'Resposta em 2 horas'
    },
    en: {
      title: 'Get your quote in 3 steps',
      step1: 'Choose equipment',
      step2: 'Where & when?',
      step3: 'Confirm',
      equipmentLabel: 'What equipment do you need?',
      cityLabel: 'City / delivery location',
      durationLabel: 'Rental duration',
      next: 'Next',
      back: 'Back',
      getQuote: 'Check availability',
      options: {
        wheelchair: 'Manual wheelchair',
        electric: 'Electric wheelchair',
        scooter: 'Mobility scooter',
        bed: 'Hospital bed',
        walker: 'Walker',
        bath: 'Bath chair'
      },
      durations: { day: '1 day', week: '1 week', month: '1 month', custom: 'More than 1 month' },
      summary: 'Request summary',
      badge: 'Response in 2 hours'
    }
  };

  const tr = t[lang] || t.pt;
  const equipKeys = Object.keys(tr.options);
  const durKeys = Object.keys(tr.durations);

  const buildSearchUrl = () => {
    const params = new URLSearchParams();
    if (equipment) params.set('equipment', equipment);
    if (city) params.set('city', city);
    if (duration) params.set('duration', duration);
    return `/start?${params.toString()}`;
  };

  return (
    <section className="py-10 px-4 bg-gradient-to-br from-slate-50 to-[#076f77]/5">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <span className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-700 text-xs font-bold px-3 py-1.5 rounded-full mb-2">
            <CheckCircle className="w-3.5 h-3.5" /> {tr.badge}
          </span>
          <h2 className="text-2xl font-bold text-slate-800">{tr.title}</h2>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition ${
                step >= s ? 'bg-[#076f77] text-white' : 'bg-slate-200 text-slate-400'
              }`}>
                {step > s ? <CheckCircle className="w-4 h-4" /> : s}
              </div>
              {s < 3 && <div className={`w-12 h-0.5 mx-1 ${step > s ? 'bg-[#076f77]' : 'bg-slate-200'}`} />}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-5">
          {/* Step 1: Equipment */}
          {step === 1 && (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-3">{tr.equipmentLabel}</label>
              <div className="grid grid-cols-2 gap-2">
                {equipKeys.map((key) => (
                  <button
                    key={key}
                    onClick={() => setEquipment(key)}
                    className={`p-3 rounded-xl border-2 text-sm font-medium transition text-left ${
                      equipment === key
                        ? 'border-[#076f77] bg-[#076f77]/10 text-[#076f77]'
                        : 'border-slate-200 hover:border-slate-300 text-slate-600'
                    }`}>
                    {tr.options[key]}
                  </button>
                ))}
              </div>
              <button
                onClick={() => equipment && setStep(2)}
                disabled={!equipment}
                className="w-full mt-4 bg-[#076f77] text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-40 hover:bg-[#055a61] transition">
                {tr.next} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Step 2: Location & Duration */}
          {step === 2 && (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">{tr.cityLabel}</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Lisboa, Porto, Coimbra..."
                className="w-full p-3 rounded-xl border-2 border-slate-200 focus:border-[#076f77] outline-none text-sm mb-4" />

              <label className="block text-sm font-semibold text-slate-700 mb-2">{tr.durationLabel}</label>
              <div className="grid grid-cols-2 gap-2">
                {durKeys.map((key) => (
                  <button
                    key={key}
                    onClick={() => setDuration(key)}
                    className={`p-3 rounded-xl border-2 text-sm font-medium transition ${
                      duration === key
                        ? 'border-[#076f77] bg-[#076f77]/10 text-[#076f77]'
                        : 'border-slate-200 hover:border-slate-300 text-slate-600'
                    }`}>
                    {tr.durations[key]}
                  </button>
                ))}
              </div>

              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 border-2 border-slate-200 text-slate-600 font-bold py-3 rounded-xl hover:bg-slate-50 transition">
                  {tr.back}
                </button>
                <button
                  onClick={() => setStep(3)}
                  disabled={!city || !duration}
                  className="flex-1 bg-[#076f77] text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-40 hover:bg-[#055a61] transition">
                  {tr.next} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Summary */}
          {step === 3 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-3">{tr.summary}</h3>
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-50">
                  <Truck className="w-4 h-4 text-[#076f77]" />
                  <span className="text-sm text-slate-700">{tr.options[equipment]}</span>
                </div>
                <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-50">
                  <Calendar className="w-4 h-4 text-[#076f77]" />
                  <span className="text-sm text-slate-700">{city} — {tr.durations[duration]}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 text-xs font-medium px-2.5 py-1 rounded-full">
                  <CheckCircle className="w-3 h-3" /> Sem fidelização
                </span>
                <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-full">
                  <Truck className="w-3 h-3" /> Entrega 24h
                </span>
                <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 text-xs font-medium px-2.5 py-1 rounded-full">
                  <CreditCard className="w-3 h-3" /> Montagem incluída
                </span>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setStep(2)}
                  className="flex-1 border-2 border-slate-200 text-slate-600 font-bold py-3 rounded-xl hover:bg-slate-50 transition">
                  {tr.back}
                </button>
                <Link
                  to={buildSearchUrl()}
                  className="flex-1 bg-[#076f77] text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 hover:bg-[#055a61] transition">
                  {tr.getQuote} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}